import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  Paper,
  Rating,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import moment from 'moment';
import {
  Link,
  useLocation,
  useParams,
} from 'react-router-dom';

import {
  DashboardError,
  DashboardPage,
  HiringTeamPanel,
} from '../../components/JobsAccount';
import { NoAccessMessage } from '../../components/NoAccessMessage';
import { book } from '../../routers/books';
import {
  useGetJobApplicantDetailQuery,
  useGetApplicantFileMutation,
  useMarkApplicantAsReadMutation,
  useUpdateApplicationStatusMutation,
  useUpdateApplicationRatingMutation,
  useAddApplicationNoteMutation,
  useDeleteApplicationNoteMutation,
  useGetJobHiringTeamQuery,
  useRequestApplicationReviewMutation,
  useResendApplicationReviewMutation,
} from '../../services/serviceJobsAccount';
import { isForbidden } from '../../utils/jobAccessError';
import { getApplicantFileErrorMessage } from '../../utils/applicantFileError';

export const JobsApplicantDetail = () => {
  const { eguid, applicationId } = useParams<{
    eguid: string;
    applicationId: string;
  }>();
  const { state } = useLocation<{ fromApplicantsPath?: string }>();
  const detail = useGetJobApplicantDetailQuery({ eguid, applicationId }, {
    skip: !eguid || !applicationId,
  });
  const [markAsRead, markResult] = useMarkApplicantAsReadMutation();
  const [getApplicantFile] = useGetApplicantFileMutation();
  const [downloadingFile, setDownloadingFile] = useState<string | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const [note, setNote] = useState('');
  const [askOpen, setAskOpen] = useState(false);
  const [askRecipient, setAskRecipient] = useState('');
  const [askEmail, setAskEmail] = useState('');
  const [askMessage, setAskMessage] = useState('');
  const [noteToDelete, setNoteToDelete] = useState<number | null>(null);
  const [updateStatus, statusResult] = useUpdateApplicationStatusMutation();
  const [updateRating, ratingResult] = useUpdateApplicationRatingMutation();
  const [addNote, addNoteResult] = useAddApplicationNoteMutation();
  const [deleteNote, deleteNoteResult] = useDeleteApplicationNoteMutation();
  const [requestReview, requestReviewResult] = useRequestApplicationReviewMutation();
  const [resendReview] = useResendApplicationReviewMutation();
  const team = useGetJobHiringTeamQuery(eguid, { skip: !eguid });
  const eligibleTeammates = (team.data?.members || [])
    .filter((member) => member.userId !== team.data?.currentUserId);
  const teamIsLoading = team.isLoading || team.isFetching;
  const hasRecipientOptions = eligibleTeammates.length > 0 || Boolean(team.data?.canManageTeam);

  const markApplicationRead = async () => {
    try {
      await markAsRead({ eguid, applicationId }).unwrap();
    } catch {
      // The mutation result renders the API error in place.
    }
  };

  const downloadFile = async (fileGuid: string) => {
    setDownloadingFile(fileGuid);
    setFileError(null);
    try {
      const response = await getApplicantFile({ eguid, fileGuid }).unwrap();
      window.location.assign(response.url);
    } catch (error) {
      setFileError(getApplicantFileErrorMessage(error));
    } finally {
      setDownloadingFile(null);
    }
  };

  const data = detail.data;
  const accessDenied = isForbidden(detail.error);
  const backPath = state?.fromApplicantsPath === book.jobsAllApplicants
    || state?.fromApplicantsPath === book.jobsNewApplicants
    ? state.fromApplicantsPath
    : book.jobsApplicants(eguid);

  const submitNote = async () => {
    if (!note.trim()) return;
    try {
      await addNote({ eguid, applicationId, note: note.trim() }).unwrap();
      setNote('');
    } catch {
      // Mutation error is rendered with the other page errors.
    }
  };

  const confirmDeleteNote = async () => {
    if (noteToDelete === null) return;
    try {
      await deleteNote({ eguid, applicationId, noteId: noteToDelete }).unwrap();
    } catch {
      // Mutation error is rendered with the other page errors.
    } finally {
      setNoteToDelete(null);
    }
  };

  const submitReviewRequest = async () => {
    const inviteByEmail = askRecipient === 'invite';
    try {
      await requestReview({
        eguid,
        applicationId,
        recipientUserId: inviteByEmail ? undefined : Number(askRecipient),
        recipientEmail: inviteByEmail ? askEmail.trim() : undefined,
        message: askMessage.trim() || undefined,
      }).unwrap();
      setAskOpen(false);
      setAskRecipient('');
      setAskEmail('');
      setAskMessage('');
    } catch {
      // Dialog keeps its values so the employer can retry.
    }
  };

  return (
    <DashboardPage
      title="View Applicant"
      isLoading={detail.isLoading || markResult.isLoading}
      noAccessComponent={accessDenied ? <NoAccessMessage /> : undefined}
      actions={(
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
          <Button
            component={Link}
            to={backPath}
            variant="outlined"
          >
            Back to Applicants
          </Button>
          {data && !data.isRead ? (
            <Button variant="contained" onClick={markApplicationRead}>
              Mark as Read
            </Button>
          ) : null}
          <HiringTeamPanel eguid={eguid} />
        </Stack>
      )}
    >
      <DashboardError error={detail.error} fallback="We could not load this application." />
      <DashboardError error={markResult.error} fallback="We could not mark this application as read." />
      <DashboardError
        error={statusResult.error || ratingResult.error || addNoteResult.error || deleteNoteResult.error || requestReviewResult.error}
        fallback="We could not update this application. Please try again."
      />
      {markResult.isSuccess ? (
        <Alert severity="success" sx={{ mb: 2 }}>Application marked as read.</Alert>
      ) : null}
      {fileError ? <Alert severity="error" sx={{ mb: 2 }}>{fileError}</Alert> : null}

      {data ? (
        <Stack spacing={3}>
          <Paper variant="outlined" sx={{ p: 3 }}>
            <Typography component="h2" variant="h6" gutterBottom>
              Applicant tracking
            </Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3}>
              <TextField
                select
                label="Stage"
                value={data.statusId}
                disabled={statusResult.isLoading}
                onChange={(event) => updateStatus({
                  eguid,
                  applicationId,
                  statusId: Number(event.target.value),
                })}
                sx={{ minWidth: 220 }}
              >
                {[
                  [1, 'New'],
                  [2, 'Reviewing'],
                  [3, 'Interview'],
                  [4, 'Offer'],
                  [5, 'Hired'],
                  [6, 'Rejected'],
                ].map(([id, label]) => (
                  <MenuItem key={id} value={id}>{label}</MenuItem>
                ))}
              </TextField>
              <Stack>
                <Typography variant="caption">Rating</Typography>
                <Rating
                  value={data.rating || 0}
                  disabled={ratingResult.isLoading}
                  onChange={(_event, value) => updateRating({
                    eguid,
                    applicationId,
                    rating: value,
                  })}
                />
              </Stack>
            </Stack>
          </Paper>
          <Paper variant="outlined" sx={{ p: 3 }}>
            <Typography component="h2" variant="h6" gutterBottom>
              Job Information
            </Typography>
            <List disablePadding>
              <ListItem disableGutters>
                <ListItemText primary="Job Title" secondary={data.jobTitle || '—'} />
              </ListItem>
              <Divider component="li" />
              <ListItem disableGutters>
                <ListItemText primary="Company Name" secondary={data.company || '—'} />
              </ListItem>
            </List>
          </Paper>

          <Paper variant="outlined" sx={{ p: 3 }}>
            <Typography component="h2" variant="h6" gutterBottom>
              Contact Information
            </Typography>
            <List disablePadding>
              <ListItem disableGutters>
                <ListItemText primary="Name" secondary={data.name || '—'} />
              </ListItem>
              <Divider component="li" />
              <ListItem disableGutters>
                <ListItemText
                  primary="Email"
                  secondary={data.email ? (
                    <Box component="a" href={`mailto:${data.email}`} sx={{ color: 'var(--accent-text)' }}>
                      {data.email}
                    </Box>
                  ) : '—'}
                />
              </ListItem>
            </List>
          </Paper>

          <Paper variant="outlined" sx={{ p: 3 }}>
            <Typography component="h2" variant="h6" gutterBottom>
              Note
            </Typography>
            <Typography color="text.secondary" sx={{ whiteSpace: 'pre-wrap' }}>
              {data.noteToEmployer || 'No note was provided.'}
            </Typography>
          </Paper>

          {data.files?.length ? (
            <Paper variant="outlined" sx={{ p: 3 }}>
              <Typography component="h2" variant="h6" gutterBottom>
                Files
              </Typography>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} flexWrap="wrap">
                {data.files.map((file) => (
                  file.isExpired ? (
                    <Button
                      key={file.jobApplicationFileId}
                      variant="outlined"
                      color="inherit"
                      startIcon={<DownloadIcon />}
                      disabled
                    >
                      {file.displayName} (Expired)
                    </Button>
                  ) : (
                    <Button
                      key={file.jobApplicationFileId}
                      variant="outlined"
                      startIcon={<DownloadIcon />}
                      disabled={downloadingFile === file.jobApplicationFileGuid}
                      onClick={() => downloadFile(file.jobApplicationFileGuid)}
                    >
                      {file.displayName}
                    </Button>
                  )
                ))}
              </Stack>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: 'block', mt: 2 }}
              >
                Resumes are available to download for 1 year after the application date.
                Older files are marked expired and can no longer be downloaded.
              </Typography>
            </Paper>
          ) : null}

          <Paper variant="outlined" sx={{ p: 3 }}>
            <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={1}>
              <Box>
                <Typography component="h2" variant="h6">Private notes</Typography>
                <Typography color="text.secondary" variant="body2">Only visible to your hiring team.</Typography>
              </Box>
              <Button variant="outlined" onClick={() => setAskOpen(true)}>Ask teammate</Button>
            </Stack>
            <Stack spacing={1.5} sx={{ my: 2 }}>
              {(data.notes || []).map((item) => (
                <Box key={item.id} sx={{ borderTop: '1px solid var(--line-soft)', pt: 1.5 }}>
                  <Stack direction="row" justifyContent="space-between" spacing={1}>
                    <Typography fontWeight={800}>{item.authorName}</Typography>
                    <Typography color="text.secondary" variant="caption">
                      {moment(item.createdDate).format('MMM D, YYYY h:mm A')}
                    </Typography>
                  </Stack>
                  {item.notifyUserName ? (
                    <Typography color="text.secondary" variant="caption">
                      Asked {item.notifyUserName} to review · {item.notificationState === 'queued' ? 'Email queued' : item.notificationState === 'needsRetry' ? 'Email needs retry' : 'No email'}
                    </Typography>
                  ) : null}
                  <Typography sx={{ mt: 0.8, whiteSpace: 'pre-wrap' }}>{item.note}</Typography>
                  <Stack direction="row" spacing={1} sx={{ mt: 0.8 }}>
                    {item.notificationState === 'needsRetry' && item.canResend ? (
                      <Button size="small" onClick={() => resendReview({ applicationId, noteId: item.id })}>
                        Retry email
                      </Button>
                    ) : null}
                    {item.canDelete ? (
                      <Button
                        size="small"
                        color="error"
                        onClick={() => setNoteToDelete(item.id)}
                      >
                        Delete
                      </Button>
                    ) : null}
                  </Stack>
                </Box>
              ))}
              {(data.notes || []).length === 0 ? (
                <Typography color="text.secondary">No private notes yet.</Typography>
              ) : null}
            </Stack>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
              <TextField
                label="Add a private note"
                value={note}
                onChange={(event) => setNote(event.target.value)}
                multiline
                minRows={2}
                fullWidth
              />
              <Button
                variant="contained"
                disabled={!note.trim() || addNoteResult.isLoading}
                onClick={submitNote}
                sx={{ whiteSpace: 'nowrap', alignSelf: 'flex-start' }}
              >
                Add note
              </Button>
            </Stack>
          </Paper>
        </Stack>
      ) : null}

      <Dialog open={askOpen} onClose={() => setAskOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Ask a teammate to review</DialogTitle>
        <DialogContent>
          {requestReviewResult.error ? <Alert severity="error" sx={{ mb: 2 }}>We could not send this request.</Alert> : null}
          {teamIsLoading ? (
            <Stack direction="row" alignItems="center" spacing={1.5} sx={{ py: 3 }}>
              <CircularProgress size={22} aria-label="Loading teammates" />
              <Typography color="text.secondary">Loading teammates…</Typography>
            </Stack>
          ) : null}
          {!teamIsLoading && team.error ? (
            <Alert severity="error" sx={{ mt: 1 }}>
              We could not load the hiring team. Close this dialog and try again.
            </Alert>
          ) : null}
          {!teamIsLoading && !team.error ? (
            <>
              {eligibleTeammates.length === 0 ? (
                <Alert severity="info" sx={{ mb: 2 }}>
                  {team.data?.canManageTeam
                    ? 'No other teammates have access yet. Choose invite by email to add someone and ask them to review.'
                    : 'No other teammates are available. A listing owner must invite someone before you can ask them to review.'}
                </Alert>
              ) : null}
              {hasRecipientOptions ? (
                <>
                  <TextField
                    select
                    label="Teammate"
                    value={askRecipient}
                    onChange={(event) => setAskRecipient(event.target.value)}
                    fullWidth
                    sx={{ mt: 1 }}
                  >
                    {eligibleTeammates.map((member) => (
                      <MenuItem key={member.userId} value={member.userId}>{member.name || member.email}</MenuItem>
                    ))}
                    {team.data?.canManageTeam ? <MenuItem value="invite">Invite by email and notify</MenuItem> : null}
                  </TextField>
                  {askRecipient === 'invite' ? (
                    <TextField
                      type="email"
                      label="Email"
                      helperText="We’ll invite this person to review applicants for this job only. New users must finish account setup first."
                      value={askEmail}
                      onChange={(event) => setAskEmail(event.target.value)}
                      fullWidth
                      sx={{ mt: 2 }}
                    />
                  ) : null}
                  <TextField
                    label="Optional message"
                    value={askMessage}
                    onChange={(event) => setAskMessage(event.target.value)}
                    multiline
                    minRows={3}
                    fullWidth
                    sx={{ mt: 2 }}
                  />
                </>
              ) : null}
            </>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAskOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={teamIsLoading || Boolean(team.error) || !askRecipient || (askRecipient === 'invite' && !askEmail.trim()) || requestReviewResult.isLoading}
            onClick={submitReviewRequest}
          >
            Send request
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={noteToDelete !== null} onClose={() => setNoteToDelete(null)} maxWidth="xs">
        <DialogTitle>Delete this note?</DialogTitle>
        <DialogContent>
          <Typography>This note will be permanently removed for your whole hiring team. This cannot be undone.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNoteToDelete(null)}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            disabled={deleteNoteResult.isLoading}
            onClick={confirmDeleteNote}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </DashboardPage>
  );
};

export default JobsApplicantDetail;
